import React from 'react';

import { useDynamicIcon } from '@lumapps/assets/hooks/useDynamicIcon';
import { CustomContentType } from '@lumapps/content-types/types';
import { useDataAttributes } from '@lumapps/data-attributes';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { standardizeTranslateObject, useTranslate } from '@lumapps/translations';

import { SpaceContentTemplateMenu } from './SpaceContentTemplateMenu';

export interface SpaceContentTypeSubmenuProps {
    /** Id of the space */
    spaceId: string;
    /** The content type */
    contentType: CustomContentType;
    /** True if the current user is super admin */
    isSuperAdmin?: boolean;
    /** Key/value object where the key is the id of the custom content type and the value tells if the user can design this content type */
    canDesignAuthorizations: Record<string, boolean>;
    /** Whether the new contribution experience is enabled or not */
    isNewContributionExperienceEnabled?: boolean;
    /** Whether the new writer editor is enabled */
    isNewWriterEditorEnabled?: boolean;
}

const SCOPE = 'space-contribution-content';

/**
 * Sub-menu of all available templates to create content from within a space.
 */
export const SpaceContentTypeSubmenu: React.FC<SpaceContentTypeSubmenuProps> = (props) => {
    const {
        spaceId,
        contentType,
        isSuperAdmin = false,
        canDesignAuthorizations,
        isNewContributionExperienceEnabled = false,
        isNewWriterEditorEnabled = false,
    } = props;
    const { get } = useDataAttributes(SCOPE);
    const { iconPath } = useDynamicIcon({ icon: contentType.icon });
    const { translateObject } = useTranslate();

    // Design authorization should be checked only if New Contribution Experience is enabled.
    // Otherwise we only check the contentType settings.
    const hasDesignAuthorization =
        isNewContributionExperienceEnabled || isNewWriterEditorEnabled
            ? Boolean(canDesignAuthorizations[contentType.id])
            : true;

    const defaultTemplateEnabled = isSuperAdmin || (!contentType.createFromScratchLocked && hasDesignAuthorization);

    const shouldOpenWriterEditor =
        isNewWriterEditorEnabled && !canDesignAuthorizations[contentType.id] && !isSuperAdmin;

    return (
        <Menu.ItemSubmenu
            label={translateObject(standardizeTranslateObject(contentType.name))}
            icon={iconPath}
            triggerProps={{ popoverProps: { minWidth: 'xxl' } }}
            {...get({ element: contentType.id })}
        >
            <SpaceContentTemplateMenu
                spaceId={spaceId}
                contentTypeId={contentType.id}
                defaultTemplateEnabled={defaultTemplateEnabled}
                scope={SCOPE}
                shouldOpenWriterEditor={shouldOpenWriterEditor}
            />
        </Menu.ItemSubmenu>
    );
};
